import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation } from '@/lib/i18n';
import { Button, Dialog, DialogContent, DialogDescription, DialogTitle, Label } from '@/components/ui';
import {
  settlementApproveSchema,
  type SettlementApproveValues,
} from '../schemas/settlementFormSchema';
import type { ApprovalStage } from '../lib/settlementActionRbac';

export interface ApproveModalProps {
  isOpen: boolean;
  stage: Exclude<ApprovalStage, 'NONE'>;
  runId: string;
  vendor: string;
  isSubmitting: boolean;
  blockedBySelfRule: boolean;
  errorMessage?: string | null;
  onConfirm: (values: SettlementApproveValues) => void;
  onCancel: () => void;
}

export const ApproveModal = ({
  isOpen,
  stage,
  runId,
  vendor,
  isSubmitting,
  blockedBySelfRule,
  errorMessage,
  onConfirm,
  onCancel,
}: ApproveModalProps) => {
  const { t } = useTranslation('settlement');
  const form = useForm<SettlementApproveValues>({
    resolver: zodResolver(settlementApproveSchema),
    defaultValues: { note: '' },
    mode: 'onBlur',
  });

  const stageLabel = stage === '1ST' ? t('settlement.approve.stage1st') : t('settlement.approve.stage2nd');

  return (
    <Dialog open={isOpen} onOpenChange={(o) => { if (!o && !isSubmitting) onCancel(); }}>
      <DialogContent data-testid="approve-modal" data-stage={stage} className="max-w-md">
        <DialogTitle>{t('settlement.approve.title', { stage: stageLabel })}</DialogTitle>
        <DialogDescription>
          {t('settlement.approve.subject', { runId, vendor })}
        </DialogDescription>

        <p
          className="mt-4 rounded-md p-3 text-sm"
          style={{
            background: stage === '2ND' ? 'var(--status-warn-bg)' : 'var(--status-info-bg)',
            color: stage === '2ND' ? 'var(--status-warn-fg)' : 'var(--status-info-fg)',
          }}
        >
          {stage === '1ST'
            ? t('settlement.approve.confirm1st')
            : t('settlement.approve.confirm2nd')}
        </p>

        {stage === '2ND' ? (
          <p
            className="mt-2 rounded-md p-2 text-xs"
            style={{
              background: 'var(--status-danger-bg)',
              color: 'var(--status-danger-fg)',
            }}
            data-testid="approve-2fa-reminder"
          >
            {t('settlement.approve.twoFaReminder')}
          </p>
        ) : null}

        {blockedBySelfRule ? (
          <p
            role="alert"
            data-testid="approve-self-rule-block"
            className="mt-2 rounded-md p-2 text-xs"
            style={{
              background: 'var(--status-danger-bg)',
              color: 'var(--status-danger-fg)',
            }}
          >
            {t('settlement.approve.selfApprovalError')}
          </p>
        ) : null}

        <form
          onSubmit={form.handleSubmit(onConfirm)}
          className="mt-4 flex flex-col gap-2"
          noValidate
        >
          <Label htmlFor="approve-note">{t('settlement.approve.noteLabel')}</Label>
          <textarea
            id="approve-note"
            {...form.register('note')}
            maxLength={500}
            rows={3}
            className="w-full rounded-md border px-3 py-2 text-sm bg-[color:var(--surface-0)] text-[color:var(--content-strong)] focus-visible:outline-none focus-visible:ring-2"
            style={{ borderColor: 'var(--content-muted)' }}
          />

          {errorMessage ? (
            <p
              role="alert"
              className="rounded-md p-2 text-sm"
              style={{
                background: 'var(--status-danger-bg)',
                color: 'var(--status-danger-fg)',
              }}
            >
              {errorMessage}
            </p>
          ) : null}

          <div className="mt-2 flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              size="md"
              onClick={onCancel}
              disabled={isSubmitting}
            >
              {t('settlement.actions.cancel')}
            </Button>
            <Button
              type="submit"
              variant="cta"
              size="md"
              disabled={isSubmitting || blockedBySelfRule}
            >
              {isSubmitting
                ? t('settlement.approve.submitting')
                : t('settlement.approve.submit')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};
